import { useEffect, useState } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Rating,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import './App.css';

const theme = createTheme({
  palette: {
    primary: { main: '#0b4f6c' },
    secondary: { main: '#ff6f3c' },
    info: { main: '#0fa3b1' },
    text: { primary: '#0e172a', secondary: '#5c6170' },
    background: { default: '#f6f3ec', paper: '#ffffff' },
  },
  shape: { borderRadius: 16 },
  typography: {
    fontFamily: '"Manrope", "PT Sans", sans-serif',
    h1: { fontFamily: '"DM Serif Display", serif', fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontFamily: '"DM Serif Display", serif', fontWeight: 700, letterSpacing: '-0.02em' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 16,
          fontWeight: 700,
          letterSpacing: '0.01em',
          boxShadow: '0 16px 32px rgba(11, 79, 108, 0.18)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          border: '1px solid rgba(14, 23, 42, 0.06)',
        },
      },
    },
  },
});

const fallbackReviews = [
  {
    id: 1,
    title: 'Колл-центр WilStream',
    subtitle: 'Устраивает',
    rating: 5,
    tag: 'Работа',
    category: 'Компании',
    username: 'Мария',
    excerpt:
      'Два года работаю оператором, нравится команда и атмосфера. Есть рост и поддержка коллег.',
    content:
      'Работаю оператором два года. Гибкий график, много смен, но команда поддерживает. Из минусов — иногда горячие линии бывают с завалами, но тимлид помогает. В офисе нормальное оборудование, связь редко лагает.',
    image:
      'https://images.unsplash.com/photo-1525182008055-f88b95ff7980?auto=format&fit=crop&w=400&q=80',
    advantages: 'Дружный коллектив, гибкий график',
    disadvantages: 'Иногда завалы на линии',
    recommend: 'Рекомендую',
    quality_score: 4,
    price_quality_score: 3.5,
    usability_score: 4,
  },
  {
    id: 2,
    title: 'Мебельная компания Мебель Микс',
    subtitle: 'Качество приятно удивило',
    rating: 4,
    tag: 'Дом и дача',
    category: 'Покупки',
    username: 'Антон',
    excerpt:
      'Сборка прошла быстро, фурнитура надежная, без резкого запаха. Сервис ответил оперативно.',
    content:
      'Заказывал шкаф и стол. Привезли через 3 дня, сборщики приехали вовремя, собрали аккуратно, мусор убрали. Материалы плотные, нет сколов. Небольшой минус — уплотнитель на дверце был примят, но заменили за день.',
    image:
      'https://images.unsplash.com/photo-1489515217757-5fd1be406fef?auto=format&fit=crop&w=400&q=80',
    advantages: 'Быстрая доставка и сборка',
    disadvantages: 'Примятый уплотнитель в комплекте',
    recommend: 'Скорее да',
    quality_score: 4.5,
    price_quality_score: 4,
    usability_score: 4,
  },
  {
    id: 3,
    title: 'Сериал «Ребёнок по расчёту», 2025',
    subtitle: 'Смотрится легко',
    rating: 3,
    tag: 'Фильмы и ТВ',
    category: 'Фильмы и ТВ',
    username: 'Ирина',
    excerpt:
      'Лёгкий семейный сюжет, актёры приятные. Для вечера под чай самое то, но без вау-эффекта.',
    content:
      'Сериал лёгкий, актеры симпатичные, серий много, но без провалов. Сюжет предсказуемый, однако для отдыха подходит. Музыка иногда громче диалогов, приходилось убавлять. Посоветую, если хочется простого фона вечером.',
    image:
      'https://images.unsplash.com/photo-1517602302552-471fe67acf66?auto=format&fit=crop&w=400&q=80',
    advantages: 'Лёгкий сюжет, приятные актёры',
    disadvantages: 'Предсказуемость и громкая музыка',
    recommend: 'Для фона — да',
    quality_score: 3,
    price_quality_score: 3,
    usability_score: 4,
  },
];

const categories = [
  'Все категории',
  'Авто и транспорт',
  'Бытовая техника',
  'Детское',
  'Компании',
  'Красота и здоровье',
  'Одежда',
  'Питание',
  'Покупки',
  'Фильмы и ТВ',
];

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const PER_PAGE = 20;

const ReviewCard = ({ review, showTag = false, onRead }) => (
  <Paper className="review-card premium-card" elevation={3}>
    {review.image ? (
      <img src={review.image} alt={review.title} className="card-img" />
    ) : null}
    <div className="card-body">
      <div className="card-top">
        {showTag && (
          <span className="tag" aria-label="Категория">
            {review.tag || review.category || 'Без категории'}
          </span>
        )}
        <Rating
          value={review.rating}
          readOnly
          precision={0.5}
          icon={<StarRoundedIcon fontSize="small" />}
          emptyIcon={<StarRoundedIcon fontSize="small" className="rating-empty" />}
        />
      </div>
      <h3>{review.title}</h3>
      <p className="muted">{review.subtitle}</p>
      <p className="excerpt">{review.excerpt}</p>
      <Button
        variant="contained"
        color="secondary"
        endIcon={<ArrowOutwardRoundedIcon />}
        sx={{ alignSelf: 'flex-start', mt: 'auto' }}
        onClick={onRead}
      >
        Читать весь отзыв
      </Button>
    </div>
  </Paper>
);

function App() {
  const [page, setPage] = useState('home');
  const [liveReviews, setLiveReviews] = useState([]);
  const [popularReviews, setPopularReviews] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [allTotalPages, setAllTotalPages] = useState(1);
  const [allPage, setAllPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [appliedCategory, setAppliedCategory] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [adminMode, setAdminMode] = useState(false);
  const [adminReviews, setAdminReviews] = useState([]);
  const [adminFilter, setAdminFilter] = useState('');
  const [selectedReview, setSelectedReview] = useState(null);
  const [detailError, setDetailError] = useState('');
  const [comments, setComments] = useState([]);
  const [commentAuthor, setCommentAuthor] = useState('');
  const [commentBody, setCommentBody] = useState('');
  const [commentStatus, setCommentStatus] = useState('');
  const [adminComments, setAdminComments] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const updateUrl = (newPage, extra = {}) => {
    const params = new URLSearchParams();
    params.set('page', newPage);
    if (extra.reviewId) params.set('reviewId', String(extra.reviewId));
    if (extra.admin === true) params.set('admin', 'true');
    const search = params.toString();
    const next = `${window.location.pathname}${search ? `?${search}` : ''}`;
    window.history.replaceState({}, '', next);
  };

  const goSection = (selector) => {
    setPage('home');
    updateUrl('home');
    setTimeout(() => {
      const el = document.querySelector(selector);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };
  const loadComments = (reviewId) => {
    if (!reviewId) return;
    fetch(`${API_URL}/reviews/${reviewId}/comments`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.comments) setComments(data.comments);
      })
      .catch(() => {});
  };
  const openReview = (review) => {
    setDetailError('');
    setSelectedReview(review);
    setPage('detail');
    updateUrl('detail', { reviewId: review?.id });
    setComments([]);
    setCommentStatus('');
    if (review?.id) {
      fetch(`${API_URL}/reviews/${review.id}`)
        .then((res) => {
          if (!res.ok) throw new Error('not found');
          return res.json();
        })
        .then((data) => {
          if (data?.review) setSelectedReview(data.review);
        })
        .catch(() => {
          setDetailError('Не удалось загрузить подробности. Попробуйте обновить страницу.');
        });
      loadComments(review.id);
    } else {
      setDetailError('Нет ID для детального просмотра.');
    }
  };
  const goHome = () => {
    setPage('home');
    updateUrl('home');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goAllReviews = () => {
    setPage('allReviews');
    updateUrl('allReviews');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goRules = () => {
    setPage('rules');
    updateUrl('rules');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goAddReview = () => {
    setPage('addReview');
    updateUrl('addReview');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goAbout = () => {
    setPage('about');
    updateUrl('about');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goPrivacy = () => {
    setPage('privacy');
    updateUrl('privacy');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goContacts = () => {
    setPage('contacts');
    updateUrl('contacts');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pageParam = params.get('page');
    const reviewId = params.get('reviewId');
    const adminParam = params.get('admin') === 'true';

    if (pageParam) {
      setPage(pageParam);
    }
    if (adminParam || pageParam === 'admin') {
      setAdminMode(true);
      updateUrl('admin', { admin: true });
    }

    if (pageParam === 'detail' && reviewId) {
      setPage('detail');
      setDetailError('');
      setComments([]);
      setCommentStatus('');
      fetch(`${API_URL}/reviews/${reviewId}`)
        .then((res) => {
          if (!res.ok) throw new Error('not found');
          return res.json();
        })
        .then((data) => {
          if (data?.review) setSelectedReview(data.review);
        })
        .catch(() => setDetailError('Не удалось загрузить подробности. Попробуйте обновить.'));
      fetch(`${API_URL}/reviews/${reviewId}/comments`)
        .then((res) => res.json())
        .then((data) => {
          if (data?.comments) setComments(data.comments);
        })
        .catch(() => {});
    }

    fetch(`${API_URL}/reviews`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.reviews) {
          setLiveReviews(data.reviews);
        }
      })
      .catch(() => {
        // fail silently, fallback data will be used
      });

    fetch(`${API_URL}/reviews/popular`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.reviews) setPopularReviews(data.reviews);
      })
      .catch(() => {});

    if (adminParam || pageParam === 'admin') {
      fetch(`${API_URL}/admin/reviews`)
        .then((res) => res.json())
        .then((data) => {
          if (data?.reviews) setAdminReviews(data.reviews);
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('page', String(allPage));
    params.set('limit', String(PER_PAGE));
    if (appliedCategory) params.set('category', appliedCategory);
    if (appliedQuery) params.set('q', appliedQuery);

    fetch(`${API_URL}/reviews?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.reviews) {
          setAllReviews(data.reviews);
          setAllTotalPages(data.totalPages || 1);
        }
      })
      .catch(() => {});
  }, [appliedCategory, appliedQuery, allPage]);

  const displayReviews = liveReviews.slice(0, 8);
  const displayPopular =
    popularReviews.length > 0
      ? popularReviews.slice(0, 8)
      : [...liveReviews].sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 8);
  const displayAll = allReviews;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="page" id="top">
        <div className="top-strip glassy">
          <Container maxWidth="xl" className="top-strip-inner">
            <button className="link-btn" type="button" onClick={() => goSection('#new')}>
              Новые отзывы
            </button>
            <button className="link-btn" type="button" onClick={() => goSection('#best')}>
              Популярные
            </button>
            <button className="link-btn" type="button" onClick={goAllReviews}>
              Все отзывы
            </button>
            <button className="link-btn" type="button" onClick={goRules}>
              Правила сайта
            </button>
            <button className="link-btn" type="button" onClick={goAddReview}>
              Добавить отзыв
            </button>
            <button className="link-btn" type="button" onClick={goAbout}>
              О компании
            </button>
            <button className="link-btn" type="button" onClick={goPrivacy}>
              Политика
            </button>
            <button className="link-btn" type="button" onClick={goContacts}>
              Контакты
            </button>
          </Container>
        </div>

        <AppBar position="sticky" elevation={0} color="inherit" className="header-shell">
          <Toolbar className="main-header">
            <div className="header-left">
              <div className="logo">
                <div className="logo-mark">Oi</div>
                <div className="logo-text">
                  <span>ОтзивИнфо</span>
                  <small>настоящие отзывы</small>
                </div>
              </div>
              <button
                className={`burger ${mobileMenuOpen ? 'open' : ''}`}
                type="button"
                onClick={() => setMobileMenuOpen((prev) => !prev)}
              >
                <span />
                <span />
                <span />
              </button>
            </div>

            <nav className={`header-links ${mobileMenuOpen ? 'open' : ''}`}>
              <button className="link-btn" type="button" onClick={goAllReviews}>
                Отзывы
              </button>
              <button className="link-btn" type="button" onClick={() => goSection('#new')}>
                Новые
              </button>
              <button className="link-btn" type="button" onClick={() => goSection('#best')}>
                Лучшие
              </button>
              <button className="link-btn" type="button" onClick={goAddReview}>
                Добавить отзыв
              </button>
              <button className="link-btn" type="button" onClick={goAbout}>
                О компании
              </button>
              <button className="link-btn" type="button" onClick={goRules}>
                Правила
              </button>
              <button className="link-btn" type="button" onClick={goPrivacy}>
                Политика
              </button>
              <button className="link-btn" type="button" onClick={goContacts}>
                Контакты
              </button>
            </nav>

            <Stack direction="row" spacing={1.5} className="header-actions">
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddCircleOutlineRoundedIcon />}
                onClick={goAddReview}
              >
                Добавить отзыв
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>

        <main className="content">
          {page === 'home' && (
            <>
              <section className="hero">
                <div className="hero-content">
                  <Container maxWidth="xl">
                    <Paper className="search glassy shadow-xl" elevation={12}>
                      <TextField
                        placeholder="Введите то, что ищете"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth
                        variant="outlined"
                        InputProps={{ sx: { borderRadius: '10px' } }}
                      />
                    <TextField
                      select
                      fullWidth
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      SelectProps={{ displayEmpty: true }}
                      InputProps={{ sx: { borderRadius: '10px' } }}
                    >
                      <MenuItem value="">Все категории</MenuItem>
                        {categories.map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Button
                        variant="contained"
                        color="info"
                        size="large"
                        startIcon={<SearchRoundedIcon />}
                        sx={{ minWidth: '140px' }}
                        onClick={() => {
                          setAppliedQuery(searchTerm.trim());
                          setAppliedCategory(categoryFilter);
                          setAllPage(1);
                          setPage('allReviews');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        Поиск
                      </Button>
                    </Paper>
                    <h1 className="hero-title">
                      Пишите отзывы и делитесь реальным опытом
                    </h1>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="large"
                      className="hero-cta"
                      onClick={goAddReview}
                    >
                      Написать отзыв
                    </Button>
                  </Container>
                </div>
              </section>

              <section className="section" id="new">
                <Container maxWidth="xl">
                  <div className="title-row">
                    <h2>Новые отзывы</h2>
                    <button className="link-btn" type="button" onClick={goAllReviews}>
                      Все отзывы
                    </button>
                  </div>
                  <div className="cards">
                    {displayReviews.map((review) => (
                      <ReviewCard
                        review={review}
                        key={`${review.id || review.title}-new`}
                        showTag
                        onRead={() => openReview(review)}
                      />
                    ))}
                  </div>
                </Container>
              </section>

              <section className="section soft" id="best">
                <Container maxWidth="xl">
                  <div className="title-row">
                    <h2>Популярные отзывы</h2>
                    <button className="link-btn" type="button" onClick={goAllReviews}>
                      Все отзывы
                    </button>
                  </div>
                  <div className="cards">
                    {displayPopular.map((review) => (
                      <ReviewCard
                        review={review}
                        key={`${review.id || review.title}-popular`}
                        onRead={() => openReview(review)}
                      />
                    ))}
                  </div>
                </Container>
              </section>
            </>
          )}

          {page === 'allReviews' && (
            <section className="section" id="all">
              <Container maxWidth="xl">
                <div className="title-row">
                  <h2>Все отзывы</h2>
                  <div className="filters-row">
                    <TextField
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      label="Поиск по названию"
                      size="small"
                    />
                    <TextField
                      select
                      size="small"
                      label="Категория"
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      sx={{ minWidth: 200 }}
                    >
                      <MenuItem value="">Все категории</MenuItem>
                      {categories
                        .filter((c) => c !== 'Все категории')
                        .map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                    </TextField>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setAppliedQuery(searchTerm.trim());
                        setAppliedCategory(categoryFilter);
                        setAllPage(1);
                      }}
                    >
                      Найти
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        setSearchTerm('');
                        setCategoryFilter('');
                        setAppliedQuery('');
                        setAppliedCategory('');
                        setAllPage(1);
                      }}
                    >
                      Сбросить
                    </Button>
                    <Button className="link-btn" type="button" onClick={goHome}>
                      На главную
                    </Button>
                  </div>
                </div>

                <div className="cards">
                  {!displayAll.length && <p className="muted">Нет отзывов по выбранным фильтрам.</p>}
                  {displayAll.map((review) => (
                    <ReviewCard
                      review={review}
                      key={`${review.id || review.title}-all`}
                      showTag
                      onRead={() => openReview(review)}
                    />
                  ))}
                </div>

                {allTotalPages > 1 && (
                  <div className="pagination">
                    <Button
                      variant="outlined"
                      onClick={() => setAllPage((p) => Math.max(1, p - 1))}
                      disabled={allPage <= 1}
                    >
                      Назад
                    </Button>
                    <span className="muted">
                      Страница {allPage} из {allTotalPages}
                    </span>
                    <Button
                      variant="outlined"
                      onClick={() => setAllPage((p) => Math.min(allTotalPages, p + 1))}
                      disabled={allPage >= allTotalPages}
                    >
                      Вперёд
                    </Button>
                  </div>
                )}
              </Container>
            </section>
          )}

          {page === 'detail' && selectedReview && (
            <section className="section" id="detail">
              <Container maxWidth="md">
                <div className="title-row">
                  <h2>{selectedReview.title}</h2>
                  <button className="link-btn" type="button" onClick={goHome}>
                    На главную
                  </button>
                </div>
                <Paper className="rules-paper" elevation={3} sx={{ p: 3 }}>
                  <div className="detail-header">
                    <div className="detail-meta">
                      <span className="tag">{selectedReview.category || 'Без категории'}</span>
                      <Rating
                        value={Number(selectedReview.rating) || 0}
                        readOnly
                        precision={0.5}
                        icon={<StarRoundedIcon fontSize="small" />}
                        emptyIcon={<StarRoundedIcon fontSize="small" className="rating-empty" />}
                      />
                      <p className="muted">
                        Автор: {selectedReview.username || 'Аноним'} ·{' '}
                        {selectedReview.created_at
                          ? new Date(selectedReview.created_at).toLocaleDateString('ru-RU')
                          : 'Дата не указана'}
                      </p>
                    </div>
                    {selectedReview.image && (
                      <img src={selectedReview.image} alt={selectedReview.title} className="detail-cover" />
                    )}
                  </div>

                  <p className="excerpt" style={{ fontSize: '18px', marginTop: '12px' }}>
                    <strong>{selectedReview.excerpt}</strong>
                  </p>

                  {(selectedReview.quality_score ||
                    selectedReview.price_quality_score ||
                    selectedReview.usability_score) && (
                    <div className="criteria-display">
                      {[
                        { label: 'Качество изготовления', value: selectedReview.quality_score },
                        {
                          label: 'Соотношение цена / качество',
                          value: selectedReview.price_quality_score,
                        },
                        { label: 'Удобство эксплуатации', value: selectedReview.usability_score },
                      ]
                        .filter((c) => c.value != null)
                        .map((crit) => (
                          <div className="criteria-row-display" key={crit.label}>
                            <div className="criteria-dots">
                              {[1, 2, 3, 4, 5].map((num) => (
                                <span
                                  key={num}
                                  className={`criteria-dot ${crit.value >= num ? 'filled' : ''}`}
                                />
                              ))}
                            </div>
                            <span className="criteria-label">{crit.label}</span>
                          </div>
                        ))}
                    </div>
                  )}

                  <div className="detail-body">
                    {(selectedReview.content || '').split(/\n+/).map((para) => (
                      <p key={para.slice(0, 20)} className="detail-text">
                        {para}
                      </p>
                    ))}
                  </div>

                  {(selectedReview.advantages || selectedReview.disadvantages || selectedReview.recommend) && (
                    <div className="pros-cons">
                      <div className="pros">
                        <h4>Достоинства:</h4>
                        {selectedReview.advantages ? (
                          <ul>
                            {selectedReview.advantages
                              .split(/\n|;/)
                              .filter((x) => x.trim().length)
                              .map((item) => (
                                <li key={item}>{item.trim()}</li>
                              ))}
                          </ul>
                        ) : (
                          <p className="muted">Не указаны</p>
                        )}
                      </div>
                      <div className="cons">
                        <h4>Недостатки:</h4>
                        {selectedReview.disadvantages ? (
                          <ul>
                            {selectedReview.disadvantages
                              .split(/\n|;/)
                              .filter((x) => x.trim().length)
                              .map((item) => (
                                <li key={item}>{item.trim()}</li>
                              ))}
                          </ul>
                        ) : (
                          <p className="muted">Не указаны</p>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedReview.recommend && (
                    <div className="recommend-block">
                      <strong>Рекомендовали бы друзьям?</strong>
                      <p>Ответ: {selectedReview.recommend}</p>
                    </div>
                  )}

                  {detailError && <p className="muted">{detailError}</p>}

                  <div className="comments-block">
                    <div className="title-row">
                      <h3>Комментарии</h3>
                      {commentStatus && <p className="muted">{commentStatus}</p>}
                    </div>
                    <div className="comment-list">
                      {comments.length === 0 && <p className="muted">Пока нет комментариев.</p>}
                      {comments.map((c) => (
                        <div className="comment-card" key={c.id || c.created_at}>
                          <div className="comment-meta">
                            <strong>{c.username || 'Аноним'}</strong>
                            <span className="muted">
                              {c.created_at
                                ? new Date(c.created_at).toLocaleString('ru-RU')
                                : ''}
                            </span>
                          </div>
                          <p className="comment-content">{c.content}</p>
                        </div>
                      ))}
                    </div>

                    {selectedReview?.id ? (
                      <form
                        className="comment-form"
                        onSubmit={async (e) => {
                          e.preventDefault();
                          if (!commentBody.trim()) {
                            setCommentStatus('Введите текст комментария.');
                            return;
                          }
                          try {
                            const res = await fetch(
                              `${API_URL}/reviews/${selectedReview.id}/comments`,
                              {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  username: commentAuthor || 'Аноним',
                                  content: commentBody,
                                }),
                              }
                            );
                            if (!res.ok) throw new Error('submit failed');
                            const data = await res.json();
                            if (data?.comment) {
                              setComments((prev) => [data.comment, ...prev]);
                            }
                            setCommentStatus('Комментарий добавлен.');
                            setCommentBody('');
                          } catch (err) {
                            // eslint-disable-next-line no-console
                            console.error('Comment submit failed', err);
                            setCommentStatus('Не удалось отправить комментарий. Попробуйте позже.');
                          }
                        }}
                      >
                        <TextField
                          label="Ваше имя"
                          fullWidth
                          value={commentAuthor}
                          onChange={(e) => setCommentAuthor(e.target.value)}
                          placeholder="Аноним"
                        />
                        <TextField
                          label="Комментарий"
                          fullWidth
                          multiline
                          minRows={3}
                          required
                          value={commentBody}
                          onChange={(e) => setCommentBody(e.target.value)}
                        />
                        <Button variant="contained" color="primary" type="submit">
                          Оставить комментарий
                        </Button>
                      </form>
                    ) : (
                      <p className="muted">Нет данных для комментирования.</p>
                    )}
                  </div>

                  <div className="detail-actions">
                    <Button variant="outlined" onClick={goAllReviews}>
                      Все отзывы
                    </Button>
                    <Button variant="contained" onClick={goHome}>
                      На главную
                    </Button>
                  </div>
                </Paper>
              </Container>
            </section>
          )}

          {page === 'addReview' && (
            <section className="section rules" id="add-review">
              <Container maxWidth="md">
                <div className="title-row">
                  <h2>Добавить отзыв</h2>
                  <button className="link-btn" type="button" onClick={goHome}>
                    На главную
                  </button>
                </div>
                <Paper className="rules-paper" elevation={3}>
                  <div className="rules-text">
                    <form
                      className="register-form"
                      onSubmit={async (e) => {
                        e.preventDefault();
                        const form = e.currentTarget;
                        const formData = new FormData(form);
                      const payload = {
                        title: formData.get('title'),
                        category: formData.get('category'),
                        username: formData.get('username'),
                        rating: formData.get('rating'),
                        excerpt: formData.get('excerpt'),
                        content: formData.get('content'),
                        image: formData.get('image'),
                        advantages: formData.get('advantages'),
                        disadvantages: formData.get('disadvantages'),
                        recommend: formData.get('recommend'),
                        qualityScore: formData.get('quality_score'),
                        priceQualityScore: formData.get('price_quality_score'),
                        usabilityScore: formData.get('usability_score'),
                      };
                        const imageFile = formData.get('imageFile');
                        if (imageFile && imageFile.size > 0) {
                          try {
                            // Ограничим загрузку до ~2 МБ, чтобы не слать огромные base64
                            if (imageFile.size > 2 * 1024 * 1024) {
                              setStatusMessage('Файл слишком большой (макс 2 МБ).');
                              return;
                            }
                            payload.image = await fileToDataUrl(imageFile);
                          } catch (err) {
                            setStatusMessage('Не удалось прочитать файл изображения.');
                            return;
                          }
                        }
                        try {
                          const res = await fetch(`${API_URL}/reviews`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(payload),
                        });
                        if (res.ok) {
                          setStatusMessage('Отправлено! После модерации отзыв появится в ленте.');
                          form.reset();
                          return;
                        }
                          const errorPayload = await res.json().catch(() => ({}));
                          const message =
                            errorPayload.error ||
                            `Не удалось отправить (код ${res.status || 'неизвестен'}). Попробуйте позже.`;
                          setStatusMessage(message);
                        } catch (err) {
                          // eslint-disable-next-line no-console
                          console.error('Submit failed', err);
                          setStatusMessage('Не удалось отправить. Проверьте подключение и повторите.');
                        }
                      }}
                    >
                      <TextField name="title" label="Заголовок" fullWidth required />
                      <TextField
                        name="category"
                        select
                        label="Категория"
                        fullWidth
                        required
                        defaultValue=""
                      >
                    <MenuItem value="" disabled>
                          Выберите категорию
                        </MenuItem>
                        {categories.map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        name="username"
                        label="Ваше имя (отобразится рядом с отзывом)"
                        fullWidth
                        required
                      />
                      <TextField
                        name="rating"
                        label="Рейтинг (0-5)"
                        type="number"
                        inputProps={{ min: 0, max: 5, step: 0.5 }}
                        fullWidth
                        required
                      />
                      <TextField
                        name="excerpt"
                        label="Краткое описание"
                        fullWidth
                        multiline
                        minRows={3}
                        required
                      />
                      <TextField
                        name="content"
                        label="Подробный отзыв"
                        fullWidth
                        multiline
                        minRows={5}
                        required
                      />
                      <div className="upload-row">
                        <label htmlFor="imageFile">Загрузить изображение (до 2 МБ)</label>
                        <input type="file" name="imageFile" id="imageFile" accept="image/*" />
                      </div>
                      <div className="criteria-row">
                        <TextField
                          name="quality_score"
                          label="Качество изготовления (0-5)"
                          type="number"
                          inputProps={{ min: 0, max: 5, step: 1 }}
                          fullWidth
                        />
                        <TextField
                          name="price_quality_score"
                          label="Соотношение цена/качество (0-5)"
                          type="number"
                          inputProps={{ min: 0, max: 5, step: 1 }}
                          fullWidth
                        />
                        <TextField
                          name="usability_score"
                          label="Удобство эксплуатации (0-5)"
                          type="number"
                          inputProps={{ min: 0, max: 5, step: 1 }}
                          fullWidth
                        />
                      </div>
                      <TextField
                        name="advantages"
                        label="Достоинства"
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder="Что понравилось? (списком через запятую или абзацем)"
                      />
                      <TextField
                        name="disadvantages"
                        label="Недостатки"
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder="Что не понравилось?"
                      />
                      <TextField
                        name="recommend"
                        select
                        label="Рекомендовали бы друзьям?"
                        fullWidth
                        defaultValue=""
                      >
                        <MenuItem value="" disabled>
                          Выберите вариант
                        </MenuItem>
                        <MenuItem value="Рекомендую">Рекомендую</MenuItem>
                        <MenuItem value="Скорее да">Скорее да</MenuItem>
                        <MenuItem value="Скорее нет">Скорее нет</MenuItem>
                        <MenuItem value="Не рекомендую">Не рекомендую</MenuItem>
                      </TextField>
                      <Button variant="contained" color="primary" size="large" type="submit">
                        Добавить Отзыв
                      </Button>
                      {statusMessage && <p className="muted">{statusMessage}</p>}
                    </form>
                  </div>
                </Paper>
              </Container>
            </section>
          )}

          {page === 'rules' && (
            <section className="section rules" id="rules">
              <Container maxWidth="xl">
                <div className="title-row">
                  <h2>Правила сайта</h2>
                  <button className="link-btn" type="button" onClick={goHome}>
                    На главную
                  </button>
                </div>
                <Paper className="rules-paper" elevation={3}>
                  <div className="rules-text">
                    <p>
                      Используя ОтзивИнфо, вы соглашаетесь с правилами сервиса и обязуетесь соблюдать
                      законодательство РФ.
                    </p>
                    <ul>
                      <li>Публикуйте честные отзывы без рекламы и оскорблений.</li>
                      <li>Не размещайте чужие персональные данные и материалы, нарушающие авторские права.</li>
                      <li>Администрация может удалять нарушения и блокировать аккаунты спамеров.</li>
                      <li>За безопасность логина и пароля отвечает Пользователь.</li>
                      <li>Запрещены деструктивные действия (накрутка, спам, фейковые отзывы).</li>
                      <li>
                        Если не согласны с правилами, прекратите использование сервиса и напишите в поддержку.
                      </li>
                    </ul>
                    <p>
                      Полная версия правил и пользовательского соглашения доступна в службе поддержки ОтзивИнфо.
                    </p>
                  </div>
                </Paper>
              </Container>
            </section>
          )}

          {page === 'contacts' && (
            <section className="section rules" id="contacts">
              <Container maxWidth="md">
                <div className="title-row">
                  <h2>Контакты</h2>
                  <button className="link-btn" type="button" onClick={goHome}>
                    На главную
                  </button>
                </div>
                <Paper className="rules-paper" elevation={3}>
                  <div className="rules-text">
                    <p>Мы рады приветствовать всех посетителей сайта otzivinfo.com!</p>
                    <p>
                      Если вы хотите связаться с нами по рекламе, сотрудничеству или вопросам работы сайта —
                      пишите на наш e-mail:
                    </p>
                    <p className="contact-mail">info@otzivinfo.com</p>
                    <p>
                      В зависимости от темы запроса вам ответит уполномоченный менеджер. Мы открыты для ваших
                      предложений. Работаем с 10:00 до 20:00 по МСК, понедельник–суббота. Воскресенье — выходной.
                    </p>
                  </div>
                </Paper>
              </Container>
            </section>
          )}

          {page === 'about' && (
            <section className="section rules" id="about-page">
              <Container maxWidth="xl">
                <div className="title-row">
                  <h2>О компании</h2>
                  <button className="link-btn" type="button" onClick={goHome}>
                    На главную
                  </button>
                </div>
                <Paper className="rules-paper" elevation={3}>
                  <div className="rules-text">
                    <p>Представителям компаний и брендов</p>
                    <p>
                      Добро пожаловать на «ОтзивИнфо». Это большой портал отзывов, где можно написать или прочитать
                      мнения о курортах, компаниях, технике, покупках, одежде, брендах, магазинах, салонах красоты,
                      отелях, автомобилях, книгах и фильмах.
                    </p>
                    <p>
                      Сайт предоставляет свободу изложения в рамках закона и этики: делитесь опытом, предупреждайте
                      о плохом сервисе и рекомендуйте хорошее. Ваш опыт помогает другим экономить время, деньги и
                      здоровье.
                    </p>
                    <p>
                      Дорогие пользователи, пишите честные и подробные отзывы, соблюдайте правила русского языка —
                      это приносит пользу сообществу.
                    </p>
                    <h3>Представителям компаний и брендов</h3>
                    <p>
                      Мы не удаляем честные отзывы только из-за их оценки. Предлагаем премодерацию отзывов, чтобы
                      защититься от ложного негатива и недобросовестной конкуренции, а также официальный профиль
                      для ответов на отзывы и общения с авторами.
                    </p>
                    <p>
                      По вопросам премодерации и регистрации официального аккаунта обращайтесь в техподдержку:
                      <span className="contact-mail"> info@otzivinfo.com</span>
                    </p>
                  </div>
                </Paper>
              </Container>
            </section>
          )}

          {page === 'privacy' && (
            <section className="section rules" id="privacy">
              <Container maxWidth="xl">
                <div className="title-row">
                  <h2>Политика конфиденциальности</h2>
                  <button className="link-btn" type="button" onClick={goHome}>
                    На главную
                  </button>
                </div>
                <Paper className="rules-paper" elevation={3}>
                  <div className="rules-text">
                    <p>Политика защиты данных пользователей otzivinfo.com</p>
                    <p>
                      Мы обрабатываем персональные данные, чтобы предоставлять сервис, обеспечивать безопасность и
                      исполнять обязательства перед пользователями. Используя сайт, вы соглашаетесь с этой
                      политикой.
                    </p>
                    <h3>Что мы собираем</h3>
                    <ul>
                      <li>
                        При регистрации: e-mail и имя пользователя (или данные соцсети) — для доступа к сервису.
                      </li>
                      <li>
                        После регистрации: отзывы, комментарии и действия в аккаунте — для персонализации и
                        модерации.
                      </li>
                      <li>
                        Техданные: IP, тип устройства, ОС, настройки браузера, cookies — для защиты и статистики.
                      </li>
                      <li>Обращения в поддержку: имя, e-mail и текст запроса — для ответа и помощи.</li>
                    </ul>
                    <h3>Как используем и защищаем</h3>
                    <ul>
                      <li>Данные не продаём. Передаём только по закону или для работы сервисов (хостинг, аналитика).</li>
                      <li>
                        Доступ к данным ограничен сотрудниками, применяются организационные и технические меры
                        защиты.
                      </li>
                      <li>Авторизация по логину и паролю, ответственность за их сохранность несёт пользователь.</li>
                    </ul>
                    <h3>Ваши права</h3>
                    <p>
                      Доступ, исправление, удаление, ограничение и возражение против обработки, сведения об
                      автоматизированных решениях. Запросы направляйте на info@otzivinfo.com.
                    </p>
                    <h3>Cookies</h3>
                    <p>
                      Используем обязательные cookies для работы сайта и аналитические (например, Yandex Metrica)
                      для улучшения сервиса. Вы можете управлять cookies в настройках браузера.
                    </p>
                    <p>
                      Актуальная версия политики публикуется на сайте. Мы можем обновлять текст, уведомления
                      размещаются на сайте.
                    </p>
                  </div>
                </Paper>
              </Container>
            </section>
          )}

          {adminMode && page === 'admin' && (
            <section className="section rules" id="admin">
              <Container maxWidth="xl">
                <div className="title-row">
                  <h2>Админка отзывов</h2>
                  <button className="link-btn" type="button" onClick={goHome}>
                    На главную
                  </button>
                </div>
                <Paper className="rules-paper" elevation={3}>
                  <div className="rules-text">
                    <div className="filters-row">
                      <TextField
                        value={adminFilter}
                        onChange={(e) => setAdminFilter(e.target.value)}
                        label="Поиск по названию/автору/категории"
                        size="small"
                        fullWidth
                      />
                    </div>
                    {!adminReviews.length && <p>Нет отзывов или нет доступа.</p>}
                    {adminReviews
                      .filter((item) => {
                        if (!adminFilter.trim()) return true;
                        const q = adminFilter.trim().toLowerCase();
                        return (
                          item.title?.toLowerCase().includes(q) ||
                          item.username?.toLowerCase().includes(q) ||
                          item.category?.toLowerCase().includes(q)
                        );
                      })
                      .map((item) => (
                      <div key={item.id} className="admin-card">
                        <div className="admin-card-header">
                          <h3>{item.title}</h3>
                          <div className="admin-badges">
                            <span className="tag">{item.status}</span>
                            {item.is_popular && <span className="tag success">Популярный</span>}
                            <span className="tag neutral">
                              Комм.: {item.comment_count != null ? item.comment_count : 0}
                            </span>
                          </div>
                        </div>
                          <p className="muted">
                            {item.username} · {item.category || 'Без категории'} · {item.rating}/5
                          </p>
                        <p className="excerpt">{item.excerpt}</p>
                        <p className="excerpt">{item.content}</p>
                        {item.image && (
                          <img src={item.image} alt={item.title} className="admin-thumb" />
                        )}
                        <div className="admin-actions">
                          <button
                            className="link-btn"
                            type="button"
                            onClick={async () => {
                              await fetch(`${API_URL}/admin/reviews/${item.id}/status`, {
                                method: 'PATCH',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ status: 'approved' }),
                              });
                              setAdminReviews((prev) =>
                                prev.map((r) =>
                                  r.id === item.id ? { ...r, status: 'approved' } : r
                                )
                              );
                            }}
                          >
                            Аппрув
                          </button>
                          <button
                            className="link-btn"
                            type="button"
                            onClick={async () => {
                              const next = !item.is_popular;
                              await fetch(`${API_URL}/admin/reviews/${item.id}/popular`, {
                                method: 'PATCH',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ isPopular: next }),
                              });
                              setAdminReviews((prev) =>
                                prev.map((r) =>
                                  r.id === item.id ? { ...r, is_popular: next } : r
                                )
                              );
                              setPopularReviews((prev) =>
                                next
                                  ? [...prev.filter((r) => r.id !== item.id), { ...item, is_popular: true }]
                                  : prev.filter((r) => r.id !== item.id)
                              );
                            }}
                          >
                            {item.is_popular ? 'Убрать из популярных' : 'В популярные'}
                          </button>
                          <button
                            className="link-btn"
                            type="button"
                            onClick={async () => {
                              await fetch(`${API_URL}/admin/reviews/${item.id}/status`, {
                                method: 'PATCH',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ status: 'rejected' }),
                              });
                              setAdminReviews((prev) =>
                                prev.map((r) =>
                                  r.id === item.id ? { ...r, status: 'rejected' } : r
                                )
                              );
                            }}
                          >
                            Отклонить
                          </button>
                          <button
                            className="link-btn"
                            type="button"
                            onClick={async () => {
                              // Toggle comments load
                              if (!adminComments[item.id]) {
                                const res = await fetch(
                                  `${API_URL}/admin/reviews/${item.id}/comments`
                                );
                                if (res.ok) {
                                  const data = await res.json();
                                  setAdminComments((prev) => ({
                                    ...prev,
                                    [item.id]: data.comments || [],
                                  }));
                                  setAdminReviews((prev) =>
                                    prev.map((r) =>
                                      r.id === item.id
                                        ? { ...r, comment_count: data.comments?.length || 0 }
                                        : r
                                    )
                                  );
                                }
                              } else {
                                setAdminComments((prev) => {
                                  const next = { ...prev };
                                  delete next[item.id];
                                  return next;
                                });
                              }
                            }}
                          >
                            {adminComments[item.id] ? 'Скрыть комментарии' : 'Комментарии'}
                          </button>
                          <button
                            className="link-btn"
                            type="button"
                            onClick={async () => {
                              await fetch(`${API_URL}/admin/reviews/${item.id}`, {
                                method: 'DELETE',
                              });
                              setAdminReviews((prev) =>
                                prev.filter((r) => r.id !== item.id)
                              );
                            }}
                          >
                            Удалить
                          </button>
                        </div>
                        {adminComments[item.id] && (
                          <div className="admin-comments">
                            {!adminComments[item.id].length && (
                              <p className="muted">Комментариев нет.</p>
                            )}
                            {adminComments[item.id].map((c) => (
                              <div className="comment-card admin" key={c.id}>
                                <div className="comment-meta">
                                  <strong>{c.username || 'Аноним'}</strong>
                                  <span className="muted">
                                    {c.created_at
                                      ? new Date(c.created_at).toLocaleString('ru-RU')
                                      : ''}
                                  </span>
                                  <button
                                    className="link-btn danger"
                                    type="button"
                                    onClick={async () => {
                                  await fetch(`${API_URL}/admin/comments/${c.id}`, {
                                    method: 'DELETE',
                                  });
                                  setAdminComments((prev) => ({
                                    ...prev,
                                    [item.id]: prev[item.id].filter((x) => x.id !== c.id),
                                  }));
                                  setAdminReviews((prev) =>
                                    prev.map((r) =>
                                      r.id === item.id
                                        ? {
                                            ...r,
                                            comment_count: Math.max(
                                              0,
                                              (r.comment_count || 0) - 1
                                            ),
                                          }
                                        : r
                                    )
                                  );
                                }}
                                  >
                                    Удалить
                                  </button>
                                </div>
                                <p className="comment-content">{c.content}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Paper>
              </Container>
            </section>
          )}
        </main>

        <footer className="footer">
          <Container maxWidth="xl">
            <div className="footer-inner">
              <div className="footer-col">
                <h4>Сервисы</h4>
                <a href="#new">Новые отзывы</a>
                <a href="#nearby">Отзывы рядом</a>
              </div>
              <div className="footer-col">
                <h4>Информация</h4>
                <button className="link-btn" type="button" onClick={goAbout}>
                  О компании
                </button>
                <button className="link-btn" type="button" onClick={goRules}>
                  Правила сайта
                </button>
                <button className="link-btn" type="button" onClick={goContacts}>
                  Контакты
                </button>
                <button className="link-btn" type="button" onClick={goPrivacy}>
                  Политика конфиденциальности
                </button>
              </div>
              <div className="footer-col">
                <h4>Мы в соцсетях:</h4>
                <div className="social-row">
                  <span className="social">vk</span>
                  <span className="social">ok</span>
                  <span className="social">tg</span>
                </div>
              </div>
            </div>
            <p className="copyright">© 2025 ОтзивИнфо. Все права защищены.</p>
          </Container>
        </footer>
      </div>
    </ThemeProvider>
  );
}

export default App;
